import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Loading, Paging, SystemText } from '@salesperformanceint/e4shared';

import Course from 'components/prism/course';
import NoResults from 'components/prism/noResults';
import Subheader from 'components/prism/subheader';
import Toggle from 'components/prism/toggle';
import { apiGet, apiPost } from 'services';
import SearchField from 'components/general/searchField';
import Rating from 'components/prism/rating';
import BehaviourCard from 'components/prism/behaviourCard';
import ManageTask from 'views/user/components/manageTask';

export const Library = ({ profile }) => {
    const pageSize = 12;
    const [page, setPage] = React.useState(1);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');
    const [opened, setOpened] = React.useState(0);
    const [showManageTask, setShowManageTask] = React.useState(false);

    const [data, setData] = React.useState({});
    const [view, setView] = React.useState('all');
    const [viewData, setViewData] = React.useState({});

    const { user } = useSelector(state => state.user);
    const { prismMultiColumn } = useSelector(state => state.ui);

    const loadData = () => {
        setLoading(true);
        apiGet(`team-member/library/${profile.id}`, user.guid, response => {
            setData(response);
            setLoading(false);
        });
    };

    React.useEffect(() => {
        loadData();
    }, []);

    React.useEffect(() => {
        if (!loading) {
            if (view === 'all') {
                const filteredData =
                    data.behaviours.filter(b => {
                        return (
                            b.name.toLowerCase().indexOf(search.toLowerCase()) >= 0 ||
                            b.area.toLowerCase().indexOf(search.toLowerCase()) >= 0
                        );
                    }) || [];

                const areas = filteredData.reduce((all, b) => {
                    if (!all.some(a => a.id === b.area_Id)) {
                        const matching = data.behaviours.filter(x => x.area_Id === b.area_Id);
                        const level = matching.reduce((sum, item) => sum + item.level, 0) / matching.length;

                        all.push({ id: b.area_Id, name: b.area, level: Math.round(level) });
                    }

                    return all;
                }, []);

                //debugger;
                setViewData({ title: text.all, data: filteredData, areas, noResults: data.behaviours?.length === 0 });
            } else if (view === 'progress') {
                setViewData({
                    title: text.progress,
                    noResultsTitle: text.inProgressNoResultsTitle,
                    noResultsSummary: text.inProgressNoResultsSummary,
                    data: data.courses.filter(x => x.progress < 100 && x.progress > 0),
                    noResults: data.behaviours?.length === 0,
                });
            } else {
                setViewData({
                    title: text.completed,
                    noResultsTitle: text.completedNoResultsTitle,
                    noResultsSummary: text.completedNoResultsSummary,
                    data: data.courses.filter(x => x.progress === 100),
                    noResults: data.behaviours?.length === 0,
                });
            }
        }
    }, [loading, data, view, search]);

    const toggleOpened = id => {
        setOpened(prev => {
            return id === prev ? 0 : id;
        });
    };

    const text = {
        all: SystemText('TeamMember/Library/All'),
        progress: SystemText('TeamMember/Library/Progress'),
        completed: SystemText('TeamMember/Library/Completed'),
        search: SystemText('TeamMember/Library/SearchPlaceholder'),
        inProgressNoResultsTitle: SystemText('TeamMember/Library/InProgress/NoResultsTitle', profile?.first_Name),
        inProgressNoResultsSummary: SystemText('TeamMember/Library/InProgress/NoResultsSummary', profile?.first_Name),
        completedNoResultsTitle: SystemText('TeamMember/Library/Completed/NoResultsTitle', profile?.first_Name),
        completedNoResultsSummary: SystemText('TeamMember/Library/Completed/NoResultsSummary', profile?.first_Name),
    };

    return (
        <div className="mt20">
            <div className="flex flex-center mb20">
                <Toggle value={view} onChange={setView}>
                    <button value="all" data-testid="toggle-all">
                        {text.all}
                    </button>
                    <button value="progress" data-testid="toggle-progress">
                        {text.progress}
                    </button>
                    <button value="completed" data-testid="toggle-completed">
                        {text.completed}
                    </button>
                </Toggle>
            </div>
            {loading ? (
                <Loading />
            ) : (
                <>
                    <Subheader title={viewData.title} count={viewData.data?.length} />
                    {viewData.noResults ? (
                        <div>
                            <NoResults
                                icon="open-book"
                                title={viewData.noResultsTitle}
                                summary={viewData.noResultsSummary}
                            />
                        </div>
                    ) : view === 'all' ? (
                        <div>
                            <div className="mb20 prism-filter">
                                <SearchField
                                    className="rounded"
                                    type="text"
                                    placeholder={text.search}
                                    onChange={setSearch}
                                    value={search}
                                />
                            </div>

                            {viewData.areas?.map(area => {
                                return (
                                    <div className="mb20">
                                        <h3 className="mb20">
                                            {area.name} <Rating level={area.level} />
                                        </h3>
                                        {viewData.data
                                            .filter(x => x.area_Id === area.id)
                                            .map(item => (
                                                <BehaviourCard
                                                    key={`behaviour-${item.id}`}
                                                    profile={profile}
                                                    behaviour={item}
                                                    opened={opened === item.id}
                                                    setOpened={toggleOpened}
                                                    setShowManageTask={setShowManageTask}
                                                />
                                            ))}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <>
                            <div className={prismMultiColumn ? 'even-col' : ''}>
                                {ArrayHelpers.getPage(viewData.data, page, pageSize).map(item => (
                                    <Course
                                        key={`course-${item.id}`}
                                        course={item}
                                        padBottom={!prismMultiColumn}
                                        managerView
                                        profileId={profile.id}
                                    />
                                ))}
                            </div>
                            {viewData.data.length > pageSize && (
                                <div className="text-center mt20">
                                    <Paging
                                        recordCount={viewData.data.length}
                                        page={page}
                                        pageSize={pageSize}
                                        goToPage={setPage}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
            <ManageTask
                show={showManageTask}
                defaultCompetency={showManageTask}
                values={{ profile_Ids: [profile.id] }}
                close={() => setShowManageTask(false)}
            />
        </div>
    );
};

export default Library;
